import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import { BrandSpinner } from "@/components/shared/ContentSpinner";
import ModuleLockGuard from "@/components/common/ModuleLockGuard";
import { FEATURE_KEYS } from "@/lib/feature-keys";

// Student shells & pages — same lazy imports as App.tsx, kept local so this
// module is self-contained. Import order mirrors the platform-host tree
// (which is the canonical source).
const LearningShell = lazy(() => import("@/components/student/shells/LearningShell"));
const ShopShell = lazy(() => import("@/components/student/shells/ShopShell"));
const ConnectShell = lazy(() => import("@/components/student/shells/ConnectShell"));

const StudentDashboard = lazy(() => import("@/pages/student/StudentDashboard"));
const StudentCourses = lazy(() => import("@/pages/student/StudentCourses"));
const StudentSessions = lazy(() => import("@/pages/student/StudentSessions"));
const StudentSubscriptions = lazy(() => import("@/pages/student/StudentSubscriptions"));
const StudentExplore = lazy(() => import("@/pages/student/StudentExplore"));
const StudentInvitesGrants = lazy(() => import("@/pages/student/StudentInvitesGrants"));
const StudentPurchases = lazy(() => import("@/pages/student/StudentPurchases"));
const MyReviews = lazy(() => import("@/pages/student/MyReviews"));
const OwnedProductDetail = lazy(() => import("@/pages/student/OwnedProductDetail"));
const StudentShopMembership = lazy(() => import("@/pages/student/StudentShopMembership.jsx"));
const StudentFreeAccess = lazy(() => import("@/pages/student/StudentFreeAccess"));
const StudentInbox = lazy(() => import("@/pages/student/StudentInbox"));
const StudentCertificates = lazy(() => import("@/pages/student/StudentCertificates"));
const StudentProfile = lazy(() => import("@/pages/student/StudentProfile"));
const StudentNotificationHistory = lazy(() => import("@/pages/student/NotificationHistory"));
const StudentSupport = lazy(() => import("@/pages/student/StudentSupport"));
const StudentSettings = lazy(() => import("@/pages/student/StudentSettings"));
const StudentGuide = lazy(() => import("@/pages/student/StudentGuide"));
const StudentNotificationSettings = lazy(() => import("@/pages/student/StudentNotificationSettings"));
const SessionRoom = lazy(() => import("@/pages/student/SessionRoom"));
const SessionRecording = lazy(() => import("@/pages/student/SessionRecording"));
const ProductDetail = lazy(() => import("@/pages/student/ProductDetail"));
const CourseViewer = lazy(() => import("@/pages/student/CourseViewer"));
const CourseCompletionPage = lazy(() => import("@/pages/student/CourseCompletionPage"));
const QuizPage = lazy(() => import("@/pages/student/QuizPage"));

/**
 * Single source of truth for the student-area internal route tree.
 *
 * Rendered identically on:
 *   - Platform host inside `/:slug` → `<AcademySlugShell />`
 *   - Custom domain inside `<CustomDomainStudentAreaGate />`
 *
 * IMPORTANT: paths here are RELATIVE (no leading slash). The parent route
 * adds the slug prefix on the platform host or roots them at "/" on the
 * custom domain. Never prefix paths with "/" or "${slug}/" — `buildStudentPath`
 * is the only place that handles host-aware URL construction.
 *
 * Adding a new student route? Add it here and it becomes available on
 * BOTH surfaces with zero duplication.
 */
export default function studentInternalRoutes() {
  return [
    <Route key="si-dashboard" path="dashboard" element={<StudentDashboard />} />,

    /* === Learning shell === */
    <Route key="si-learning" path="learning" element={<LearningShell />}>
      <Route index element={<Navigate to="courses" replace />} />
      <Route path="courses" element={<StudentCourses />} />
      <Route path="courses/:courseId" element={<CourseViewer />} />
      <Route path="courses/:courseId/completed" element={<CourseCompletionPage />} />
      <Route
        path="courses/:courseId/quiz/:quizId"
        element={
          <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.QUIZZES}>
            <QuizPage />
          </ModuleLockGuard>
        }
      />
      <Route
        path="sessions"
        element={
          <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
            <StudentSessions />
          </ModuleLockGuard>
        }
      />
      <Route
        path="free-access"
        element={
          <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.LIMITED_FREE_ACCESS}>
            <StudentFreeAccess />
          </ModuleLockGuard>
        }
      />
    </Route>,

    /* === Shop shell === */
    <Route key="si-shop" path="shop" element={<ShopShell />}>
      <Route index element={<Navigate to="my-purchases" replace />} />
      <Route path="my-purchases" element={<StudentPurchases />} />
      <Route path="my-purchases/:productId" element={<OwnedProductDetail />} />
      <Route path="explore" element={<StudentExplore />} />
      <Route path="explore/:productId" element={<ProductDetail />} />
      <Route
        path="membership"
        element={
          <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.VIP_MEMBERSHIP}>
            <StudentShopMembership />
          </ModuleLockGuard>
        }
      />
    </Route>,

    /* === Connect shell === */
    <Route key="si-connect" path="connect" element={<ConnectShell />}>
      <Route index element={<Navigate to="inbox" replace />} />
      <Route path="inbox" element={<StudentInbox />} />
      <Route
        path="grants"
        element={
          <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.GRANTS_AND_INVITES}>
            <StudentInvitesGrants />
          </ModuleLockGuard>
        }
      />
      <Route
        path="invites"
        element={
          <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.GRANTS_AND_INVITES}>
            <StudentInvitesGrants />
          </ModuleLockGuard>
        }
      />
      <Route path="notifications" element={<StudentNotificationHistory />} />
      <Route path="settings/notifications" element={<StudentNotificationSettings />} />
      <Route path="support" element={<StudentSupport />} />
    </Route>,

    /* === Other student pages === */
    <Route key="si-my-subs" path="my-subscriptions" element={<StudentSubscriptions />} />,
    <Route key="si-my-reviews" path="my-reviews" element={<MyReviews />} />,
    <Route key="si-my-memberships" path="my-memberships" element={<Navigate to="../shop/membership" replace />} />,
    <Route
      key="si-certificates"
      path="certificates"
      element={
        <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.CERTIFICATES_AUTO}>
          <StudentCertificates />
        </ModuleLockGuard>
      }
    />,
    <Route key="si-profile" path="profile" element={<StudentProfile />} />,
    <Route key="si-guide" path="guide" element={<StudentGuide />} />,
    <Route key="si-settings" path="settings" element={<StudentSettings />} />,

    /* === Sessions === */
    <Route
      key="si-session"
      path="session/:sessionId"
      element={
        <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
          <SessionRoom />
        </ModuleLockGuard>
      }
    />,
    <Route
      key="si-session-quiz"
      path="session/:sessionId/quiz/:quizId"
      element={
        <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
          <QuizPage />
        </ModuleLockGuard>
      }
    />,
    <Route
      key="si-session-recording"
      path="session/:sessionId/recording"
      element={
        <ModuleLockGuard mode="student" featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
          <SessionRecording />
        </ModuleLockGuard>
      }
    />,
  ];
}

// Suppress unused import warning — BrandSpinner kept available for callers
// that wrap this tree with their own Suspense boundary.
export { BrandSpinner };